import { computed } from "@preact/signals-react";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AvisConstants } from "../../data/avisConstants";
import { Api } from "../../generated/Api";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { AvisUtils, TimeUtils, apiConfig } from "../../utils";
import { AvisContext } from "../shared/context/AvisContext";
import { ErrorLoadingDataHoc } from "../shared/error/ErrorLoadingDataHoc";
import { sessionTimeRemaining } from "./SessionStates";
import { AvisModal } from "../shared/components/modal";

interface IUserSessionActivityProps {
  children?: React.ReactNode;
}

export function UserSessionActivityHoc(
  props: Readonly<IUserSessionActivityProps>,
) {
  const { children } = props;
  const { setupAxiosInterceptors, logout, res, ctxPortalSettings } =
    useContext(AvisContext);
  const multiplyMinToMs = 60 * 1000;

  const [, setIsSessionExpired] = useSessionStorage(
    AvisConstants.session_expired_key,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isSessionExpiring, setIsSessionExpiring] = useState<boolean>(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
  const [firstSetup, setFirstSetup] = useState<boolean>(false);

  const getLastActivity = (): Date | null => {
    const storageValue = localStorage.getItem(
      AvisConstants.user_last_activity_time_key,
    );

    if (!storageValue) return null;

    const timestamp = parseInt(storageValue);

    return new Date(timestamp);
  };

  const timeoutMinutes = useMemo(
    () => ctxPortalSettings?.timeout,
    [ctxPortalSettings],
  );

  const timeToLive = useMemo(
    () => (timeoutMinutes ? timeoutMinutes * multiplyMinToMs : undefined),
    [timeoutMinutes, multiplyMinToMs],
  ); // time to live in milliseconds

  const sessionRemainingTimeMs = useMemo(() => {
    return ctxPortalSettings?.sessionNotificationTimer
      ? ctxPortalSettings.sessionNotificationTimer * multiplyMinToMs
      : 0;
  }, [ctxPortalSettings, multiplyMinToMs]);

  const getRemainingtimeToLive = (): number | null => {
    if (!timeToLive) {
      if (!isLoading) setIsLoading(true);

      return null;
    }

    if (isLoading) {
      setIsLoading(false);
    }

    const currentDate = moment(new Date());
    const lastActivity = getLastActivity();

    if (!lastActivity) return timeToLive;

    const parsedActivity = moment(lastActivity);

    const dateDiff = moment(currentDate).diff(parsedActivity, "milliseconds");

    let timeToLiveRemaining = timeToLive - dateDiff;

    if (timeToLiveRemaining < 0) timeToLiveRemaining = 0;

    return timeToLiveRemaining;
  };

  const handleSessionExpired = () => {
    setIsSessionExpired(1);
    logout();
  };

  const checkUserActivity = useCallback(() => {
    const lastActivity = getLastActivity();

    if (!lastActivity) return;

    const timeToLiveRemaining = getRemainingtimeToLive();

    if (timeToLiveRemaining !== null) {
      sessionTimeRemaining.value = timeToLiveRemaining;

      if (timeToLiveRemaining <= sessionRemainingTimeMs) {
        setIsSessionExpiring(true);
      }

      if (timeToLiveRemaining > sessionRemainingTimeMs) {
        setIsSessionExpiring(false);
      }

      if (timeToLiveRemaining <= 0) handleSessionExpired();
    }

    if (!firstSetup) setFirstSetup(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionRemainingTimeMs]);

  useEffect(() => {
    if (!ctxPortalSettings?.timeout) return;

    checkUserActivity();
    setupAxiosInterceptors();

    if (!getLastActivity()) AvisUtils.updateLastActivityTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxPortalSettings]);

  useEffect(() => {
    if (!timeToLive) return;

    const interval = setInterval(checkUserActivity, 1000); // update each second

    return () => clearInterval(interval);
  }, [timeToLive, checkUserActivity]);

  useEffect(
    () => setIsWarningModalOpen(isSessionExpiring),
    [isSessionExpiring],
  );

  const updateLastActivity = () => {
    const apiWithoutInterceptors = new Api(apiConfig);

    apiWithoutInterceptors
      .securityClaims()
      .then(() => {
        AvisUtils.updateLastActivityTime();
        checkUserActivity();
      })
      .catch(logout);
  };

  const timeRemaining = computed(() =>
    TimeUtils.millisecondsToHHMMSS(sessionTimeRemaining.value),
  );

  return (
    <ErrorLoadingDataHoc loading={isLoading || !firstSetup} fullPageLoader>
      <AvisModal
        className={"avis-session-modal"}
        title={res("session_ModalWarningTitle")}
        isOpen={isWarningModalOpen}
        confirmButtonText={res("button_Continue")}
        cancelButtonText={res("button_Exit")}
        onConfirm={updateLastActivity}
        onClose={logout}
        modalHeight="auto"
      >
        <div>
          <p>{res("session_SessionWillExpireIn", timeRemaining.value)}</p>
          <br />
          <p>{res("session_ModalWarningContent")}</p>
        </div>
      </AvisModal>
      {children}
    </ErrorLoadingDataHoc>
  );
}
